import { ArrowDownRight, ArrowUpRight, CalendarDays } from "lucide-react";
import type { BasSummary } from "@/types/tax";
import { Alert } from "@/components/ui/Alert";
import { Badge } from "@/components/ui/Badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { currency } from "@/lib/utils";

export function BasSummaryCard({ summary }: { summary: BasSummary }) {
  const isPayable = summary.netGst >= 0;
  const auditNotes = uniqueAuditNotes(summary.auditNotes);
  const rows = [
    { label: "G1 Total sales", mobileLabel: "G1 Sales", value: summary.g1TotalSales, variant: "outline" as const },
    { label: "1A GST on sales", mobileLabel: "1A GST", value: summary.gstOnSales1A, variant: "payable" as const },
    { label: "1B GST credits", mobileLabel: "1B GST credits", value: summary.gstOnPurchases1B, variant: "credit" as const },
  ];

  return (
    <Card>
      <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <CardTitle>BAS worksheet</CardTitle>
          <CardDescription>
            {summary.period.label}, {summary.period.startDate} to {summary.period.endDate}
          </CardDescription>
        </div>
        <Badge variant={isPayable ? "payable" : "credit"}>{isPayable ? "Payable" : "Refundable"}</Badge>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="rounded-lg border border-border bg-surface p-3 sm:p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-muted">Net GST {isPayable ? "payable" : "refundable"}</p>
              <p className="tabular mt-1 text-2xl font-semibold tracking-tight text-ink sm:text-3xl">{currency(Math.abs(summary.netGst))}</p>
            </div>
            <div className="flex size-12 items-center justify-center rounded-xl bg-white text-primary shadow-sm">
              {isPayable ? <ArrowUpRight className="size-6" /> : <ArrowDownRight className="size-6" />}
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2 text-xs text-muted sm:hidden">
            <CalendarDays className="size-4" aria-hidden="true" />
            Due <span className="font-semibold text-ink">{summary.period.dueDate}</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {rows.map((row) => (
            <div key={row.label} className="rounded-lg border border-border bg-white p-2 sm:p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="whitespace-nowrap text-[0.68rem] font-semibold leading-4 text-muted sm:hidden">{row.mobileLabel}</p>
                <p className="hidden text-xs font-semibold leading-4 text-muted sm:block">{row.label}</p>
                <Badge variant={row.variant} className="hidden sm:inline-flex">{row.label.split(" ")[0]}</Badge>
              </div>
              <p className="tabular mt-2 text-sm font-semibold text-ink sm:mt-3 sm:text-xl">{currency(row.value)}</p>
            </div>
          ))}
        </div>

        <div className="hidden items-center gap-2 rounded-lg bg-surface px-3 py-2 text-sm text-muted sm:flex">
          <CalendarDays className="size-4" aria-hidden="true" />
          Due date: <span className="font-semibold text-ink">{summary.period.dueDate}</span>
        </div>

        {auditNotes.slice(0, 3).map((note) => (
          <Alert key={note.id} variant={note.severity === "danger" ? "danger" : note.severity === "warning" ? "warning" : "info"} title={note.title}>
            {note.message}
          </Alert>
        ))}
      </CardContent>
    </Card>
  );
}

function uniqueAuditNotes(notes: BasSummary["auditNotes"]) {
  const seen = new Set<string>();
  return notes.filter((note) => {
    const key = `${note.severity}|${note.title}|${note.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
